'use client';

import { c as _c } from "react/compiler-runtime";
import { environmentVariables } from '@/config/environment-variables';
import { flushPersonalData } from '@/lib/flush-personal-data';
import { withKeyvalStore } from '@/lib/idb-keyval-store';
import { makeQueryClient } from '@/trpc/query-client';
import { deserializePersistedClient, PERSISTED_QUERY_CACHE_MAX_AGE, PERSISTER_THROTTLE_MS, serializePersistedClient, shouldPersistQuery } from '@/trpc/query-persistence';
import type { AppRouter } from '@/trpc/routers/_app';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClientContext } from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { httpBatchLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { signOut } from 'next-auth/react';
import React, { useState } from 'react';
import superjson from 'superjson';

/** `/entrypoint` is excluded from the i18n rewrites, so it never carries a locale prefix. */
const ENTRYPOINT_PATH = '/entrypoint';

/**
 * Guards against running the sign out more than once: a batched request can
 * produce several concurrent 401s, and `/entrypoint` itself talks to tRPC while
 * syncing offline content - without this, an unauthenticated call from there
 * would reload the page in a loop.
 */
let isHandlingUnauthenticated = false;

/**
 * Custom fetch function that handles 401 Unauthorized responses.
 * When a 401 is received, it clears auth cookies via signOut and redirects to /entrypoint.
 */
const fetchWithAuthRedirect: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 401 && !isHandlingUnauthenticated) {
    isHandlingUnauthenticated = true;
    flushPersonalData({
      keepUnsentChatMessages: true
    });
    try {
      await signOut({
        redirect: false
      });
    } catch (error) {
      console.error('[TRPC] Sign out failed during 401 handling:', error);
    } finally {
      if (globalThis.location.pathname === ENTRYPOINT_PATH) {
        // already there - allow a later 401 to retry the cleanup
        isHandlingUnauthenticated = false;
      } else {
        globalThis.location.href = ENTRYPOINT_PATH;
      }
    }
    // Return the response anyway to prevent further processing
    return response;
  }
  return response;
};
export const trpc = createTRPCReact<AppRouter>();
export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;
const getUrl = (): string => {
  const base = ((): string => {
    if (typeof globalThis !== 'undefined') return '';
    if (environmentVariables.NEXT_PUBLIC_APP_HOST_URL !== '') return environmentVariables.NEXT_PUBLIC_APP_HOST_URL;
    return 'http://localhost:3000';
  })();
  return `${base}/api/trpc`;
};

/**
 * Ceiling for a batched query URL, in characters.
 *
 * `httpBatchLink` defaults to `Infinity`: however many queries happen to resolve in the same tick
 * all go out as one GET, and the URL grows without bound. Nothing in the deployment tolerates
 * that. Requests reach the app through Traefik and then a per-stack nginx, and nginx is the
 * tightest hop by a wide margin — neither `client_header_buffer_size` nor
 * `large_client_header_buffers` is set in `nginx/nginx.conf`, so its defaults apply and the
 * request line has to fit a single 8k buffer. Past that nginx answers `414 Request-URI Too Large`
 * and the whole batch fails, taking every unrelated query that shared the tick with it. (Traefik
 * allows ~1 MiB via Go's `MaxHeaderBytes` default and Node allows 16 KB, so neither is reached.)
 *
 * 6000 sits ~2100 characters below the 8179 that survives `GET ` + ` HTTP/1.1`. The headroom
 * matters in the other direction too: a batch is split until each part fits, but a *single*
 * operation that cannot fit throws instead, and `schedule.getCourseStatuses` legitimately carries
 * 40 ids in one input. This has to stay comfortably above that, so lower it with care.
 *
 * Only GET batches are measured; mutations carry their input in a POST body and are unaffected.
 */
const MAX_BATCH_URL_LENGTH = 6000;
const createHttpBatchLink = (): ReturnType<typeof httpBatchLink> => {
  return httpBatchLink({
    url: getUrl(),
    transformer: superjson,
    maxURLLength: MAX_BATCH_URL_LENGTH,
    fetch: fetchWithAuthRedirect as unknown as NonNullable<Parameters<typeof httpBatchLink>[0]['fetch']>
  });
};

/**
 * Setup static persister which falls back to no-op on Server Side Rendering.
 * This guarantees consistent component mounting during hydration and prevents warnings.
 */
/* eslint-disable unicorn/no-null */
const indexedDBStorage = {
  getItem: async (key: string): Promise<string | null> => {
    let value: string | undefined;
    await withKeyvalStore('readonly', store => {
      const request = store.get(key);
      request.onsuccess = (): void => {
        value = request.result as string | undefined;
      };
    });
    return value ?? null;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await withKeyvalStore('readwrite', store => {
      store.put(value, key);
    });
  },
  removeItem: async (key: string): Promise<void> => {
    await withKeyvalStore('readwrite', store => {
      store.delete(key);
    });
  }
};
/* eslint-enable unicorn/no-null */

const persister: Persister =
// eslint-disable-next-line unicorn/prefer-global-this
typeof window === 'undefined' ? {
  persistClient: (): Promise<void> => Promise.resolve(),
  restoreClient: (): Promise<undefined> => Promise.resolve() as Promise<undefined>,
  removeClient: (): Promise<void> => Promise.resolve()
} : createAsyncStoragePersister({
  storage: indexedDBStorage,
  throttleTime: PERSISTER_THROTTLE_MS,
  serialize: serializePersistedClient,
  deserialize: deserializePersistedClient,
  key: 'conveniat-query-cache-idb'
});
const persistOptions = {
  persister,
  maxAge: PERSISTED_QUERY_CACHE_MAX_AGE,
  dehydrateOptions: {
    shouldDehydrateQuery: shouldPersistQuery
  }
};
export const TRPCProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(7);
  const {
    children
  } = t0;
  const [queryClient] = useState(_temp);
  const [trpcClient] = useState(_temp2);
  let t1;
  if ($[0] !== children || $[1] !== queryClient) {
    t1 = <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>{children}</PersistQueryClientProvider>;
    $[0] = children;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== queryClient || $[4] !== t1 || $[5] !== trpcClient) {
    t2 = <trpc.Provider client={trpcClient} queryClient={queryClient}>{t1}</trpc.Provider>;
    $[3] = queryClient;
    $[4] = t1;
    $[5] = trpcClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/**
 * Returns the tRPC utils, or undefined when rendered outside {@link TRPCProvider}.
 *
 * Detection goes through `QueryClientContext` because @trpc/react-query v11 does not export a
 * context to read. The previous implementation looked for `TRPCContext` on the module, which
 * does not exist there, so it resolved to undefined for every caller and silently disabled
 * every consumer — `refreshAndOptimisticallyUpdateChat` never received utils. TRPCProvider
 * renders PersistQueryClientProvider, so a QueryClient in scope means the tRPC provider is too.
 */
export function useOptionalTrpcUtils(): ReturnType<typeof trpc.useUtils> | undefined {
  const queryClient = React.useContext(QueryClientContext);
  if (queryClient === undefined) {
    return undefined;
  }
  return trpc.useUtils();
}
function _temp() {
  return makeQueryClient();
}
function _temp2() {
  return trpc.createClient({
    links: [createHttpBatchLink()]
  });
}